import React, { useState, useEffect, useRef, memo } from "react";
import ReactPlayer from "react-player";
import { CircularProgress, Box, Typography } from "@mui/material";
import { environment } from "@shared/config/environment";

interface DocDataProps {
  docData: {
    id?: string | number;
    azure_blob_url?: string;
  };
}

const VideoPlayer = memo(
  ({ docData }: DocDataProps) => {
    const [videoSrc, setVideoSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [isPlaying, setIsPlaying] = useState<boolean>(false); // ✅ Track video playing state
    const playerRef = useRef<ReactPlayer | null>(null); // ✅ Store ReactPlayer ref

    const prevDocIdRef = useRef<number | string | undefined>(undefined); // Track previous ID
    const prevUrlRef = useRef<string | null>(null); // ✅ Track last loaded URL

    // ✅ Construct the base video URL
    const basic_url = `${environment?.baseUrl}/data/LMS/module/UploadData?operation_value=${docData?.azure_blob_url}`;

    useEffect(() => {
      // ✅ If the video is playing and the URL is the same, DO NOT update
      if (isPlaying && prevUrlRef.current === basic_url) {
        // console.log("🚫 Video is playing. Preventing unnecessary reload.");
        return;
      }

      // ✅ If the blob URL changes, stop the current video
      if (prevUrlRef.current && prevUrlRef.current !== basic_url) {
        // console.log("⚠️ Stopping current video and loading new one.");
        setIsPlaying(false); // ✅ Force stop video playback
      }

      // ✅ Prevent API call if the video ID hasn't changed
      if (!docData?.azure_blob_url || prevDocIdRef.current === docData.id) {
        // console.log("🚫 Preventing unnecessary fetch. Video already loaded.");
        return;
      }

      // ✅ Fetch New Video
      const fetchVideo = async () => {
        try {
          setLoading(true);
          // console.log("🔄 Fetching new video:", docData.id);
          setVideoSrc(basic_url);
          prevDocIdRef.current = docData.id; // ✅ Store current ID
          prevUrlRef.current = basic_url; // ✅ Store current URL
        } catch (error) {
          console.error("❌ Error fetching video:", error);
          setVideoSrc(null);
        } finally {
          setLoading(false);
        }
      };

      fetchVideo();
    }, [docData.id, docData.azure_blob_url, isPlaying]); // ✅ Watch `blob_url` changes

    return (
      <Box sx={{ textAlign: "center", my: 4, position: "relative", width: "100%" }}>
        <Typography variant="h5">MP4 Video Player</Typography>

        {loading && <CircularProgress sx={{ mt: 2 }} />}

        {videoSrc ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "70vh",
            }}
          >
            <ReactPlayer
              ref={playerRef} // ✅ Store the player reference
              key={videoSrc} // ✅ Force re-render when URL changes
              url={videoSrc}
              controls
              playing={isPlaying}
              width="80vw"
              height="70vh"
              onPlay={() => setIsPlaying(true)} // ✅ Track when video starts playing
              onPause={() => setIsPlaying(false)} // ✅ Reset when paused
              onEnded={() => setIsPlaying(false)} // ✅ Reset when video ends
            />
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            No video available.
          </Typography>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // console.log(
    //   "Comparing prevProps and nextProps:",
    //   prevProps.docData?.azure_blob_url,
    //   nextProps.docData?.azure_blob_url
    // );
    return prevProps.docData?.azure_blob_url === nextProps.docData?.azure_blob_url;
  }
);

export default VideoPlayer;

